import PageToolbar from '@/components/linear/page-toolbar';
import { editCompanyPath, notificationPreferencePath } from '@/lib/linear-paths';
import { SHORTCUTS } from '@/lib/shortcuts';
import { readWorkspaceTheme, setWorkspaceTheme, type WorkspaceTheme } from '@/lib/theme';
import type { SharedPageProps } from '@/types/shared';
import { Form, Link, usePage } from '@inertiajs/react';
import { useState } from 'react';

interface NotificationPrefs {
  inbox: boolean;
}

const NOTIF_KEY = 'caprock:notifications';

function readNotifications(): NotificationPrefs {
  try {
    const raw = localStorage.getItem(NOTIF_KEY);
    if (!raw) return { inbox: true };
    const parsed = JSON.parse(raw) as Partial<NotificationPrefs>;
    return {
      inbox: parsed.inbox !== false,
    };
  } catch {
    return { inbox: true };
  }
}

export default function Show() {
  const { currentCompany, companies, currentUser } = usePage<SharedPageProps>().props;
  const url = usePage().url;
  const tab = new URLSearchParams(url.split('?')[1] ?? '').get('tab');
  const [theme, setTheme] = useState<WorkspaceTheme>(() =>
    typeof window === 'undefined' ? 'dark' : readWorkspaceTheme()
  );
  const [notifications, setNotifications] = useState<NotificationPrefs>(() =>
    typeof window === 'undefined' ? { inbox: true } : readNotifications()
  );
  const emailNotifications = currentUser?.emailNotifications !== false;

  const chooseTheme = (next: WorkspaceTheme) => {
    setTheme(next);
    setWorkspaceTheme(next);
  };

  const toggleNotification = (key: keyof NotificationPrefs) => {
    const next = { ...notifications, [key]: !notifications[key] };
    setNotifications(next);
    localStorage.setItem(NOTIF_KEY, JSON.stringify(next));
  };

  if (tab === 'preferences') {
    return (
      <div className="ln-page">
        <PageToolbar title="Preferences" showNew={false} />
        <div className="ln-page-scroll" style={{ padding: 24, flex: 1, maxWidth: 720 }}>
          <div className="ln-settings-row">
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--ln-muted)' }}>Theme</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                {(['dark', 'light', 'system'] as WorkspaceTheme[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    className="ln-toolbar-btn"
                    data-active={theme === option ? 'true' : 'false'}
                    onClick={() => chooseTheme(option)}
                    style={
                      theme === option
                        ? { background: 'var(--ln-selected)', color: 'var(--ln-text)' }
                        : undefined
                    }
                  >
                    {option[0].toUpperCase() + option.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (tab === 'notifications') {
    return (
      <div className="ln-page">
        <PageToolbar title="Notifications" showNew={false} />
        <div className="ln-page-scroll" style={{ padding: 24, flex: 1, maxWidth: 720 }}>
          <NotificationRow
            label="Inbox notifications"
            enabled={notifications.inbox}
            onToggle={() => toggleNotification('inbox')}
          />
          <Form method="patch" action={notificationPreferencePath()} className="ln-settings-row">
            {({ processing }) => (
              <>
                <div style={{ fontWeight: 510 }}>Email notifications</div>
                <input
                  type="hidden"
                  name="emailNotifications"
                  value={emailNotifications ? 'false' : 'true'}
                />
                <button
                  type="submit"
                  className="ln-toolbar-btn"
                  disabled={processing}
                  style={{ marginLeft: 'auto' }}
                >
                  {emailNotifications ? 'On' : 'Off'}
                </button>
              </>
            )}
          </Form>
        </div>
      </div>
    );
  }

  if (tab === 'shortcuts') {
    const groups = Array.from(new Set(SHORTCUTS.map((item) => item.group)));
    return (
      <div className="ln-page">
        <PageToolbar title="Shortcuts" showNew={false} />
        <div className="ln-page-scroll" style={{ padding: 24, flex: 1, maxWidth: 720 }}>
          {groups.map((group) => (
            <div key={group} style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: '0.9375rem', fontWeight: 590, marginBottom: 8 }}>{group}</h2>
              {SHORTCUTS.filter((item) => item.group === group).map((item) => (
                <div key={`${item.group}-${item.keys}`} className="ln-shortcuts-row">
                  <span className="ln-kbd">{item.keys}</span>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="ln-page">
      <PageToolbar title="Settings" showNew={false} />
      <div className="ln-page-scroll" style={{ padding: 24, flex: 1, maxWidth: 720 }}>
        <h2 style={{ fontSize: '0.9375rem', fontWeight: 590, marginBottom: 16 }}>Workspace</h2>
        <div className="ln-settings-row">
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--ln-muted)' }}>Name</div>
            <div style={{ fontWeight: 510 }}>{currentCompany?.name ?? 'CAP Rock Labs'}</div>
          </div>
          {currentCompany ? (
            <Link
              href={editCompanyPath(currentCompany.id)}
              className="ln-toolbar-btn"
              style={{ marginLeft: 'auto' }}
            >
              Edit
            </Link>
          ) : null}
        </div>
        <div className="ln-settings-row">
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--ln-muted)' }}>URL</div>
            <div className="ln-mono" style={{ fontSize: '0.8125rem', color: 'var(--ln-muted)' }}>
              /{currentCompany?.slug ?? 'workspace'}
            </div>
          </div>
        </div>

        {companies.length > 1 ? (
          <div style={{ marginTop: 32 }}>
            <h2 style={{ fontSize: '0.9375rem', fontWeight: 590, marginBottom: 16 }}>
              All workspaces
            </h2>
            {companies.map((company) => (
              <div key={company.id} className="ln-settings-row">
                <div style={{ fontWeight: 510 }}>{company.name}</div>
                {company.isDefault ? <span className="ln-label">Default</span> : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function NotificationRow({
  label,
  enabled,
  onToggle,
}: {
  label: string;
  enabled: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="ln-settings-row">
      <div style={{ fontWeight: 510 }}>{label}</div>
      <button
        type="button"
        className="ln-toolbar-btn"
        onClick={onToggle}
        style={{ marginLeft: 'auto' }}
      >
        {enabled ? 'On' : 'Off'}
      </button>
    </div>
  );
}
